import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Alert, TextField } from '@mui/material';

import { AppSetting } from "../../../types/appSetting";
import { IExpense } from "../../../types/expense";

import { AppSettingService } from '../../../services/appSettingService';
import { ErrorProvider } from "../../../classes/errorProvider";
import { ExpenseService } from "../../../services/expenseService";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";
import ExpenseTable from "../../tables/expenseTable";

import "../../../styles/entity/expenseList.scss";

const ExpenseList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<IExpense[]>();

	//Filtering
	const [appSettings, setAppSettings] = useState<AppSetting[]>([]);
	const [nameFilter, setNameFilter] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [spreadAcrossMaterialsValue, setspreadAcrossMaterialsValue] = useState<string>("-1");
	const [spreadAcrossMaterialsFilter, setspreadAcrossMaterialsFilter] = useState<boolean | undefined>(undefined);
	const [activeFilterValue, setActiveFilterValue] = useState<string>("1");
	const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

	const [isLoadingAppSettings, setIsLoadingAppSettings] = useState(true);
	const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const loadExpenses = async () => {
		setIsLoadingExpenses(true);
		setErrorMessage(undefined);

		ExpenseService.getExpenses()
			.then((response) => {
				console.log('ExpenseService.getExpenses: SUCCESS', response);
				let expenses = ExpenseService.mapExpenses(response.data);

				setGridView(expenses);
			})
			.catch((error) => {
				console.log('ExpenseService.getExpenses: FAILURE', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setIsLoadingExpenses(false);
			});
	}

	const getAppSettings = async () => {
		setIsLoadingAppSettings(true);
		AppSettingService.getAppSettings()
			.then((response) => {
				console.log('AppSettingService.getAppSettings: SUCCESS', response)
				setAppSettings(response.data);
			})
			.catch((error) => {
				console.log('AppSettingService.getAppSettings: ERROR', error)
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setIsLoadingAppSettings(false);
			});
	}

	useEffect(() => {
		getAppSettings();
		loadExpenses();
	}, [])

	useEffect(() => {
		if (appSettings?.length > 0) {
			// Do something with settings here if required
		}
	}, [appSettings])

	useEffect(() => {
		setspreadAcrossMaterialsFilter(spreadAcrossMaterialsValue == "-1" ? undefined : (spreadAcrossMaterialsValue == "1" ? true : false));
	}, [spreadAcrossMaterialsValue])

	useEffect(() => {
		setActiveFilter(activeFilterValue == "-1" ? undefined : (activeFilterValue == "1" ? true : false));
	}, [activeFilterValue])

	return (
		<div className="hwi-expense-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageExpenses || userContext.permissions?.canViewExpensesOnly}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Expenses</div>
							<div>{userContext.permissions?.canManageExpenses && <NavLink to="/expense/add"><Button variant="outlined">New Expense</Button></NavLink>}</div>
						</div>
						<section className="search-options">
							<FormControl className="name-filter" size="small">
								<TextField
									InputLabelProps={{ shrink: true }}
									label="Name"
									name="name"
									value={nameFilter}
									size="small"
									onChange={(event) => { setNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl className="category-filter" size="small">
								<TextField
									InputLabelProps={{ shrink: true }}
									label="Category"
									name="category"
									value={categoryFilter}
									size="small"
									onChange={(event) => { setCategoryFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Distribute Cost</InputLabel>
								<Select
									value={spreadAcrossMaterialsValue}
									label="Distribute Cost"
									onChange={(event) => { setspreadAcrossMaterialsValue(event.target.value) }}
								>
									<MenuItem value="-1" key="-1">All</MenuItem>
									<MenuItem value="1" key="1">Yes</MenuItem>
									<MenuItem value="0" key="0">No</MenuItem>
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Active</InputLabel>
								<Select
									value={activeFilterValue}
									label="Active"
									onChange={(event) => { setActiveFilterValue(event.target.value) }}
								>
									<MenuItem value="-1" key="-1">All</MenuItem>
									<MenuItem value="1" key="1">Active</MenuItem>
									<MenuItem value="0" key="0">Inactive</MenuItem>									
								</Select>
							</FormControl>			
						</section>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingExpenses || isLoadingAppSettings} text="Loading Expenses...">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error">
									{errorMessage}
								</Alert>
							}
							{!errorMessage &&
								<ExpenseTable expenses={gridView} name={nameFilter} category={categoryFilter} spreadAcrossMaterials={spreadAcrossMaterialsFilter} isActive={activeFilter}></ExpenseTable>
							}
						</Spinner>
					</section>
				</PermissionGate>
			</Container>
		</div>
	);
};

export default ExpenseList;